import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { financialTransactionAPI } from './financialTransactionService';
import AddExpenseComponent from './AddExpenseComponent';
import ExpenseSummaryHeaderComponent from './components/ExpenseSummaryHeaderComponent';
import BalanceSummaryComponent from './components/BalanceSummaryComponent';
import TransactionSummaryComponent from './components/TransactionSummaryComponent';
import MyTransactionActivityComponent from './components/MyTransactionActivityComponent';
import SettleUpModal from './modal/SettleUpModal';
import AddGroupExpenseModal from './modal/AddGroupExpenseModal';
import AddDonationModal from './modal/AddDonationModal';
import ChangePoolOwnerModal from './modal/ChangePoolOwnerModal';
import { Loader } from 'lucide-react';

const SEASON_ORDER = {
  Year: 0, H2: 1, H1: 2, Q4: 3, Q3: 4, Q2: 5, Q1: 6, Winter: 7, Fall: 8, Summer: 9, Spring: 10,
};

const CURRENT_YEAR = new Date().getFullYear();

/**
 * FinanceTab — entity-agnostic finance module.
 *
 * Props:
 *   entityId   – ID used as `useBy` in all finance API calls
 *   entityType – type string passed as `useByType` (default: 'Group')
 *   entity     – entity object; must expose `isMember` and `userRole`
 *   members    – array of members passed to AddExpenseComponent
 */
const FinanceTab = ({ entityId, entityType = 'Group', entity, members }) => {
  const { theme } = useTheme();
  const navigate  = useNavigate();

  const currentUserId = String(JSON.parse(localStorage.getItem('socialUser') || '{}').id || '');

  // ── State ──────────────────────────────────────────────────────────────────
  const [entityBalances, setEntityBalances]       = useState(null);
  const [yearBalances, setYearBalances]           = useState({});
  const [expandedYears, setExpandedYears]         = useState({ [CURRENT_YEAR]: true });
  const [allTransactions, setAllTransactions]     = useState([]);
  const [loadingBalances, setLoadingBalances]     = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [showCreateExpense, setShowCreateExpense] = useState(false);
  const [showGroupExpense, setShowGroupExpense]   = useState(false);
  const [showDonation, setShowDonation]           = useState(false);
  const [editingExpense, setEditingExpense]       = useState(null);
  const [seasonFilter, setSeasonFilter]           = useState(null);
  const [settleUpTarget, setSettleUpTarget]       = useState(null);
  const [activeSubTab, setActiveSubTab]           = useState('overview');
  const [showChangePoolOwner, setShowChangePoolOwner] = useState(false);

  // ── API calls ──────────────────────────────────────────────────────────────
  const loadTransactions = () => {
    if (!entityId) return;
    setLoadingTransactions(true);
    financialTransactionAPI.getByGroupId(entityId)
      .then(data => setAllTransactions(Array.isArray(data) ? data : []))
      .catch(() => setAllTransactions([]))
      .finally(() => setLoadingTransactions(false));
  };

  const loadBalances = (filter = null) => {
    if (!entityId) return;
    setLoadingBalances(true);
    const params = { useBy: entityId, useByType: entityType };
    if (filter) {
      params.season     = filter.season;
      params.seasonYear = filter.seasonYear;
    }
    financialTransactionAPI.getBalances(params)
      .then(b => setEntityBalances(b))
      .catch(() => setEntityBalances(null))
      .finally(() => setLoadingBalances(false));
  };

  // Idempotent per-year balance fetch (ref tracks which years are in-flight or done)
  const fetchedYearsRef = useRef(new Set());

  const loadYearBalances = useCallback((year) => {
    if (!entityId || fetchedYearsRef.current.has(year)) return;
    fetchedYearsRef.current.add(year);
    setYearBalances(prev => ({ ...prev, [year]: { members: [], loading: true } }));
    financialTransactionAPI.getBalances({ useBy: entityId, useByType: entityType, seasonYear: year })
      .then(b => setYearBalances(prev => ({ ...prev, [year]: { members: b?.members || [], loading: false } })))
      .catch(() => setYearBalances(prev => ({ ...prev, [year]: { members: [], loading: false } })));
  }, [entityId, entityType]);

  const toggleYear = (year) => {
    const nowExpanded = !expandedYears[year];
    setExpandedYears(prev => ({ ...prev, [year]: nowExpanded }));
    if (nowExpanded) loadYearBalances(year);
  };

  const refreshAll = () => {
    loadTransactions();
    loadBalances(seasonFilter);
    setYearBalances({});
    fetchedYearsRef.current = new Set();
    if (expandedYears[CURRENT_YEAR]) loadYearBalances(CURRENT_YEAR);
  };

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchedYearsRef.current = new Set();
    setYearBalances({});
  }, [entityId]);

  useEffect(() => {
    if (!entityId) return;
    loadTransactions();
    loadBalances(null);
  }, [entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (allTransactions.length === 0) return;
    const years = [...new Set(allTransactions.map(t => t.seasonYear).filter(Boolean))];
    setExpandedYears(Object.fromEntries(years.map(y => [y, true])));
    years.forEach(year => loadYearBalances(year));
  }, [allTransactions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSeasonChange = (filter) => {
    setSeasonFilter(filter);
    loadBalances(filter);
  };

  // ── Derived data ───────────────────────────────────────────────────────────
  const seasonChips = useMemo(() => {
    const seen = new Map();
    allTransactions.forEach(t => {
      if (t.season && t.seasonYear) {
        const key = `${t.seasonYear}-${t.season}`;
        if (!seen.has(key)) seen.set(key, { season: t.season, seasonYear: t.seasonYear });
      }
    });
    return Array.from(seen.values()).sort((a, b) => {
      if (b.seasonYear !== a.seasonYear) return b.seasonYear - a.seasonYear;
      return (SEASON_ORDER[a.season] ?? 99) - (SEASON_ORDER[b.season] ?? 99);
    });
  }, [allTransactions]);

  const uniqueYears = useMemo(() => {
    const years = new Set(allTransactions.map(t => t.seasonYear).filter(Boolean));
    return Array.from(years).sort((a, b) => b - a);
  }, [allTransactions]);

  const displayTransactions = useMemo(() => {
    if (!seasonFilter) return allTransactions;
    return allTransactions.filter(
      t => t.season === seasonFilter.season && t.seasonYear === seasonFilter.seasonYear
    );
  }, [allTransactions, seasonFilter]);

  const groupedTransactions = useMemo(() => {
    if (seasonFilter !== null || displayTransactions.length === 0) return null;
    const yearMap = new Map();
    displayTransactions.forEach(t => {
      const year   = t.seasonYear || 0;
      const season = t.season || 'Uncategorized';
      if (!yearMap.has(year)) yearMap.set(year, new Map());
      const seasonMap = yearMap.get(year);
      if (!seasonMap.has(season)) seasonMap.set(season, []);
      seasonMap.get(season).push(t);
    });
    return Array.from(yearMap.entries())
      .sort(([a], [b]) => b - a)
      .map(([year, seasonMap]) => {
        const seasonGroups = Array.from(seasonMap.entries())
          .sort(([a], [b]) => (SEASON_ORDER[a] ?? 99) - (SEASON_ORDER[b] ?? 99))
          .map(([season, txns]) => ({ season, txns }));
        return { year, seasonGroups, totalCount: seasonGroups.reduce((n, g) => n + g.txns.length, 0) };
      });
  }, [displayTransactions, seasonFilter]);

  const expenseManagerUserId = entity?.expenseManagerUserId ?? null;
  const adminName = entity?.expenseManagerName || null;

  const balanceMembers = entityBalances?.members || [];

  // Group Surplus = total already PAID to the expense manager
  // Group Lagging = total still unpaid to expense manager
  const groupSurplus = useMemo(() => {
    if (!expenseManagerUserId) return 0;
    return displayTransactions.flatMap(t => t.creditTransactions || [])
      .filter(ct => {
        const toId = ct.toUserId ?? ct.toUser?.id;
        return toId === expenseManagerUserId;
      })
      .reduce((sum, ct) => {
        const paid = ct.paidAmount ?? parseFloat(ct.paid_amount || '0');
        return sum + paid;
      }, 0);
  }, [displayTransactions, expenseManagerUserId]);

  // Pool Expenses = CTs where toUserId is null (expense from pool)
  const poolExpenses = useMemo(() => {
    return displayTransactions
      .flatMap(t => t.creditTransactions || [])
      .filter(ct => ct.toUserId == null && ct.toUser == null)
      .reduce((sum, ct) => sum + (ct.amount || 0), 0);
  }, [displayTransactions]);

  const groupLagging = useMemo(() => {
    if (!expenseManagerUserId) return 0;
    return displayTransactions
      .flatMap(t => t.creditTransactions || [])
      .filter(ct => {
        const toId = ct.toUserId ?? ct.toUser?.id;
        return toId === expenseManagerUserId && ct.status !== 'paid';
      })
      .reduce((sum, ct) => {
        const paid = ct.paidAmount ?? parseFloat(ct.paid_amount || '0');
        return sum + Math.max(0, ct.amount - paid);
      }, 0);
  }, [displayTransactions, expenseManagerUserId]);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loadingBalances && !entityBalances) {
    return (
      <div className={`${theme.card} rounded-2xl border ${theme.border} p-10 flex flex-col items-center gap-3`}>
        <Loader className="w-8 h-8 text-blue-500 animate-spin" />
        <p className={`${theme.textSecondary} text-sm`}>Loading finances…</p>
      </div>
    );
  }

  const myBalance = balanceMembers.find(m => String(m.userId) === currentUserId);
  const totalOwed = myBalance?.owed || 0;
  const totalOwe  = myBalance?.owes || 0;

  return (
    <div className="space-y-4">

      {/* Sub-tab switcher */}
      <div className="flex gap-2">
        {[
          { id: 'overview',    label: 'Overview' },
          { id: 'my-activity', label: 'My Activity' },
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
              activeSubTab === tab.id
                ? 'bg-blue-500 text-white border-blue-500'
                : `${theme.backgroundSecondary} ${theme.textSecondary} ${theme.border} hover:border-blue-400`
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeSubTab === 'my-activity' && (
        <MyTransactionActivityComponent
          allTransactions={allTransactions}
          currentUserId={currentUserId}
          totalOwed={totalOwed}
          totalOwe={totalOwe}
          theme={theme}
        />
      )}

      {activeSubTab === 'overview' && (
        <>
          <ExpenseSummaryHeaderComponent
            seasonChips={seasonChips}
            seasonFilter={seasonFilter}
            onSeasonChange={handleSeasonChange}
            groupSurplus={groupSurplus}
            groupLagging={groupLagging}
            poolExpenses={poolExpenses}
            adminName={adminName}
            hasMembers={(members || []).length > 0}
            hasTransactions={allTransactions.length > 0}
            isAdmin={entity?.userRole === 'admin'}
            onChangePoolOwner={() => setShowChangePoolOwner(true)}
            theme={theme}
          />

          <BalanceSummaryComponent
            seasonFilter={seasonFilter}
            uniqueYears={uniqueYears}
            yearBalances={yearBalances}
            expandedYears={expandedYears}
            onToggleYear={toggleYear}
            balanceMembers={balanceMembers}
            loadingBalances={loadingBalances}
            currentUserId={currentUserId}
            onSettleUp={setSettleUpTarget}
            theme={theme}
          />

          <TransactionSummaryComponent
            displayTransactions={displayTransactions}
            groupedTransactions={groupedTransactions}
            isAdmin={entity?.userRole === 'admin'}
            isMember={entity?.isMember}
            currentUserId={currentUserId}
            onAddContribution={() => setShowCreateExpense(true)}
            onAddExpense={() => setShowGroupExpense(true)}
            onAddDonation={() => setShowDonation(true)}
            onEdit={txn => setEditingExpense(txn)}
            onSettleUp={setSettleUpTarget}
            onDeleted={refreshAll}
            loadingTransactions={loadingTransactions}
            entityId={entityId}
            navigate={navigate}
            theme={theme}
          />
        </>
      )}

      <SettleUpModal
        target={settleUpTarget}
        onClose={() => setSettleUpTarget(null)}
        onSuccess={refreshAll}
        theme={theme}
      />

      {showCreateExpense && (
        <AddExpenseComponent
          theme={theme}
          groupId={entityId}
          members={members}
          currentUserId={currentUserId}
          onClose={() => setShowCreateExpense(false)}
          onSaved={() => { setShowCreateExpense(false); refreshAll(); }}
        />
      )}

      {editingExpense && (
        <AddExpenseComponent
          theme={theme}
          groupId={entityId}
          members={members}
          currentUserId={currentUserId}
          expense={editingExpense}
          onClose={() => setEditingExpense(null)}
          onSaved={() => { setEditingExpense(null); refreshAll(); }}
        />
      )}

      {showGroupExpense && (
        <AddGroupExpenseModal
          theme={theme}
          groupId={entityId}
          expenseManagerId={expenseManagerUserId}
          onClose={() => setShowGroupExpense(false)}
          onSaved={() => { setShowGroupExpense(false); refreshAll(); }}
        />
      )}

      {showDonation && (
        <AddDonationModal
          theme={theme}
          groupId={entityId}
          adminId={expenseManagerUserId}
          members={members}
          onClose={() => setShowDonation(false)}
          onSaved={() => { setShowDonation(false); refreshAll(); }}
        />
      )}

      {showChangePoolOwner && (
        <ChangePoolOwnerModal
          theme={theme}
          groupId={entityId}
          members={members}
          currentExpenseManagerId={entity?.expenseManagerId}
          onClose={() => setShowChangePoolOwner(false)}
          onSaved={() => { setShowChangePoolOwner(false); refreshAll(); }}
        />
      )}
    </div>
  );
};

export default FinanceTab;
